import { ScreenSize } from 'glints-aries';
import { DropShadow } from 'glints-aries/lib/@next';
import { borderRadius16 } from 'glints-aries/lib/@next/utilities/borderRadius';
import { Blue, Neutral, Yellow } from 'glints-aries/lib/@next/utilities/colors';
import styled from 'styled-components';

import {
  CARD_SHADOW_HOVER,
  MOBILE_BREAKPOINT,
  SGGradients,
} from '../constants';
import { sgCtaBase } from './shared.sc';

export { SGInner as Inner } from './shared.sc';

export const Section = styled.section`
  position: relative;
  z-index: 3;
  margin-top: -40px;
  padding: 64px 0;
  background: linear-gradient(180deg, ${Neutral.B100} 0%, ${Neutral.B99} 100%);

  @media (max-width: ${ScreenSize.tablet}px) {
    padding: 48px 0;
    margin-top: -32px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 36px 0;
    margin-top: -24px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: ${ScreenSize.tablet}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

interface CardProps {
  audience: 'talent' | 'employer' | 'ai';
}

export const Card = styled.div<CardProps>`
  position: relative;
  overflow: hidden;
  background: ${Neutral.B100};
  border: thin solid ${Neutral.B85};
  border-radius: ${borderRadius16};
  box-shadow: ${DropShadow.card};
  padding: 0;
  display: flex;
  flex-direction: column;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.25s ease;
    background: ${({ audience }) =>
      audience === 'employer' ? Yellow.Brand : Blue.Brand};
  }

  &:hover,
  &:focus-within {
    transform: translateY(-4px);
    box-shadow: ${CARD_SHADOW_HOVER};
    border-color: ${Blue.Brand};
  }

  &:hover::before,
  &:focus-within::before {
    transform: scaleX(1);
  }

  @media (prefers-reduced-motion: reduce) {
    transition:
      box-shadow 0.2s ease,
      border-color 0.2s ease;

    &:hover,
    &:focus-within {
      transform: none;
    }
  }
`;

export const Art = styled.div<CardProps>`
  padding: 26px 28px 0;
  background: ${({ audience }) =>
    audience === 'employer'
      ? SGGradients.cardHeaderYellow
      : SGGradients.cardHeaderBlue};
  border-bottom: thin solid ${Neutral.B85};

  svg {
    display: block;
    width: 100%;
    height: auto;
    max-width: 340px;
    margin: 0 auto;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 18px 18px 0;
  }
`;

export const Body = styled.div`
  padding: 28px 36px 36px;
  display: flex;
  flex: 1;
  flex-direction: column;

  @media (max-width: ${ScreenSize.tablet}px) {
    padding: 24px 28px 28px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px 20px 24px;
  }
`;

export const Title = styled.h2`
  font-size: 22px;
  margin: 0 0 10px;
  color: ${Neutral.B18};
  letter-spacing: -0.01em;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 19px;
  }
`;

export const Description = styled.p`
  color: ${Neutral.B40};
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

export const Cta = styled.a<CardProps>`
  ${sgCtaBase}
  background: ${({ audience }) =>
    audience === 'employer' ? Yellow.Brand : Blue.Brand};
  color: ${({ audience }) =>
    audience === 'employer' ? Neutral.B18 : Neutral.B100};
  align-self: flex-start;
  margin-top: auto;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`;
